import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

const LAYOUT_DETAILED_REFUNDS = 'detailed-refunds';
const LAYOUT_REFUNDS_BY_USER = 'refunds-by-user';

type PaymentRefundRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   method: string;
   reason: string;
   receipt: string;
   amount: string;
   amountRaw: number;
   user: string;
   hasRealTyallsRowId: boolean;
};

type RefundUserSummary = {
   user: string;
   count: number;
   totalAmount: number;
};

const PAYMENT_TRANSACTION_DESCRIPTIONS: Record<string, string> = {
   '9000': 'Cash',
   '9003': 'Direct Bill',
   '9104': 'American Express - OPI',
   '9105': 'American Express POS',
   '9124': 'Visa Card - EFT',
   '9125': 'Visa POS',
   '9134': 'Mastercard - EFT',
   '9135': 'Master Card POS',
   '9161': 'EFTPOS POS',
   '9164': 'EFTPOS - OPI',
   '9190': 'Direct Bank Deposit',
   '9400': 'T/A Prepaid commission',
   '9405': 'T/A Prepaid Commission Fore',
   '9950': 'Cash Drop'
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function getPathValue(source: any, path: string): any {
   if (!source || !path) return undefined;

   return path.split('.').reduce((current, part) => current?.[part], source);
}

function getFirstValue(source: any, paths: string[]): any {
   for (const path of paths) {
      const value = getPathValue(source, path);

      if (value !== null && value !== undefined && value !== '') {
         return value;
      }
   }

   return undefined;
}

function getFirstString(source: any, paths: string[]): string {
   return asString(getFirstValue(source, paths));
}

function toNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/^-\s*/, '-');

   const amount = Number(cleaned);
   return Number.isFinite(amount) ? amount : null;
}

function getFirstNumber(source: any, paths: string[]): number | null {
   for (const path of paths) {
      const amount = toNumberOrNull(getPathValue(source, path));

      if (amount !== null) return amount;
   }

   return null;
}

function formatCurrency(value: number): string {
   const formatted = Math.abs(value).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
   if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(
         2,
         '0'
      )}/${slashMatch[3]}`;
   }

   return raw;
}

function formatTime(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoTimeMatch = raw.match(/T(\d{2}:\d{2})/);
   if (isoTimeMatch) return isoTimeMatch[1];

   const simpleTimeMatch = raw.match(/^(\d{1,2}):(\d{2})/);
   if (simpleTimeMatch) {
      return `${simpleTimeMatch[1].padStart(2, '0')}:${simpleTimeMatch[2]}`;
   }

   return raw;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      getFirstString(row, [
         'ReceiptNumber',
         'receiptNumber',
         'ReceiptNo',
         'receiptNo',
         'TransactionNo',
         'transactionNo',
         'Id',
         'id'
      ]) ||
      `payment-refund:${getFirstString(row, ['Date', 'date'])}:${getFirstString(row, [
         'RoomNumber',
         'roomNumber'
      ])}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function getSourceRows(reportJson: any): { rows: any[]; isOracle: boolean } {
   const parsed = parseReportJson(reportJson);

   if (Array.isArray(parsed?.PaymentRefunds)) {
      return { rows: parsed.PaymentRefunds, isOracle: false };
   }

   if (Array.isArray(parsed?.Rows)) {
      return { rows: parsed.Rows, isOracle: false };
   }

   if (Array.isArray(parsed?.rows)) {
      return { rows: parsed.rows, isOracle: false };
   }

   if (Array.isArray(parsed?.Postings)) {
      return { rows: parsed.Postings, isOracle: false };
   }

   if (Array.isArray(parsed?.financialPostings)) {
      return { rows: parsed.financialPostings, isOracle: true };
   }

   if (Array.isArray(parsed?.journalPostings?.postings)) {
      return { rows: parsed.journalPostings.postings, isOracle: true };
   }

   if (Array.isArray(parsed?.JournalPostings?.Postings)) {
      return { rows: parsed.JournalPostings.Postings, isOracle: true };
   }

   return { rows: [], isOracle: false };
}

/**
 * Saved Tyalls shape:
 * {
 *   Debit: 0,
 *   Credit: -9.28
 * }
 *
 * Refund rule:
 * - negative Credit = refund
 * - positive Debit = refund out, shown as negative
 */
function amountFromMappedPostingDollars(row: any): number | null {
   const credit = getFirstNumber(row, [
      'Credit',
      'credit',
      'CreditAmount',
      'creditAmount',
      'CreditAmount.Amount',
      'creditAmount.amount'
   ]);

   if (credit !== null && credit < 0) {
      return credit;
   }

   const debit = getFirstNumber(row, [
      'Debit',
      'debit',
      'DebitAmount',
      'debitAmount',
      'DebitAmount.Amount',
      'debitAmount.amount'
   ]);

   if (debit !== null && debit > 0) {
      return -Math.abs(debit);
   }

   const amount = getFirstNumber(row, ['Amount', 'amount', 'AmountRaw', 'amountRaw']);

   return amount !== null && amount < 0 ? amount : null;
}

function amountFromOraclePostingDollars(row: any): number | null {
   const amount = getFirstNumber(row, [
      'amount.amount',
      'postedAmount.amount',
      'Amount',
      'amount'
   ]);

   if (amount !== null && amount < 0) {
      return amount;
   }

   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount > 0) {
      return -Math.abs(debitAmount);
   }

   const creditAmount = getFirstNumber(row, [
      'creditAmount.amount',
      'CreditAmount.Amount',
      'CreditAmount',
      'creditAmount'
   ]);

   if (creditAmount !== null && creditAmount < 0) {
      return creditAmount;
   }

   return null;
}

function getDate(row: any): string {
   return getFirstString(row, [
      'Date',
      'date',
      'TransactionDate',
      'transactionDate',
      'PostingDate',
      'postingDate',
      'BusinessDate',
      'businessDate'
   ]);
}

function getTime(row: any): string {
   return getFirstString(row, [
      'Time',
      'time',
      'TransactionTime',
      'transactionTime',
      'lastModifyDateTime',
      'LastModifyDateTime'
   ]);
}

function getRoom(row: any): string {
   return getFirstString(row, [
      'RoomNumber',
      'roomNumber',
      'RoomNo',
      'roomNo',
      'Room',
      'room',
      'guestInfo.roomId',
      'guestInfo.roomNumber'
   ]);
}

function getGuestName(row: any): string {
   return getFirstString(row, [
      'GuestName',
      'guestName',
      'Name',
      'name',
      'guestInfo.guestName'
   ]);
}

function getTransactionCode(row: any): string {
   return getFirstString(row, [
      'TransactionCode',
      'transactionCode',
      'TrnCode',
      'trnCode'
   ]);
}

function getMethod(row: any): string {
   const transactionCode = getTransactionCode(row);

   return (
      getFirstString(row, [
         'PaymentType',
         'paymentType',
         'PaymentGroup',
         'paymentGroup',
         'PaymentSubGroup',
         'paymentSubGroup',
         'TransactionCodeDescription',
         'transactionCodeDescription',
         'TransactionDescription',
         'transactionDescription'
      ]) ||
      PAYMENT_TRANSACTION_DESCRIPTIONS[transactionCode] ||
      transactionCode
   );
}

function getReason(row: any): string {
   const values = [
      getFirstString(row, [
         'Reason',
         'reason',
         'Description',
         'description',
         'Remark',
         'remark',
         'Reference',
         'reference',
         'SupplementReferenceCreditCardNo',
         'supplementReferenceCreditCardNo'
      ])
   ].filter(Boolean);

   return Array.from(new Set(values)).join(' ');
}

function getReceipt(row: any): string {
   const receipt = getFirstString(row, [
      'ReceiptNumber',
      'receiptNumber',
      'ReceiptNo',
      'receiptNo',
      'TransactionNo',
      'transactionNo'
   ]);

   if (!receipt) return '';

   return receipt.startsWith('#') ? receipt : `#${receipt}`;
}

function getUser(row: any): string {
   const direct = getFirstString(row, [
      'User',
      'user',
      'CashId',
      'cashId',
      'PostedByCashier',
      'postedByCashier',
      'CashierId',
      'cashierId',
      'cashierInfo.cashierId'
   ]);

   if (direct) return direct;

   return getFirstString(row, ['UserName', 'userName', 'cashierInfo.userName']);
}

function normaliseRefundRows(
   sourceRows: any[],
   isOracle: boolean
): PaymentRefundRow[] {
   const rows: PaymentRefundRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw >= 0) continue;

      const dateRaw = getDate(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         date: formatDate(dateRaw),
         dateRaw,
         time: formatTime(getTime(sourceRow)),
         room: getRoom(sourceRow),
         guestName: getGuestName(sourceRow),
         method: getMethod(sourceRow),
         reason: getReason(sourceRow),
         receipt: getReceipt(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getUser(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function sortRefundRows(rows: PaymentRefundRow[]): PaymentRefundRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);
      if (dateCompare !== 0) return dateCompare;

      return a.time.localeCompare(b.time);
   });
}

function getRefundRows(reportJson: any): PaymentRefundRow[] {
   const { rows: sourceRows, isOracle } = getSourceRows(reportJson);

   return sortRefundRows(normaliseRefundRows(sourceRows, isOracle));
}

function buildRefundUserSummaries(rows: PaymentRefundRow[]): RefundUserSummary[] {
   const totals = new Map<string, RefundUserSummary>();

   for (const row of rows) {
      const user = row.user || 'Unknown';

      const current = totals.get(user) ?? {
         user,
         totalAmount: 0,
         count: 0
      };

      current.totalAmount += row.amountRaw;
      current.count += 1;

      totals.set(user, current);
   }

   return Array.from(totals.values()).sort((a, b) => {
      const aNumber = Number(a.user);
      const bNumber = Number(b.user);

      if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
         return aNumber - bNumber;
      }

      return a.user.localeCompare(b.user);
   });
}

function flattenDetailedRefunds(reportJson: any): ReportRowVm[] {
   const refundRows = getRefundRows(reportJson);

   return refundRows.map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'transaction',
         date: row.date,
         dateRaw: row.dateRaw,
         time: row.time,
         room: row.room,
         guestName: row.guestName,
         method: row.method,
         reason: row.reason,
         receipt: row.receipt,
         amount: row.amount,
         amountRaw: row.amountRaw,
         user: row.user
      }
   }));
}

function flattenRefundsByUser(reportJson: any): ReportRowVm[] {
   const refundRows = getRefundRows(reportJson);
   const userSummaries = buildRefundUserSummaries(refundRows);

   return userSummaries.map((summary) => ({
      rowId: `payment-refunds:${LAYOUT_REFUNDS_BY_USER}:user:${summary.user}`,
      isAnnotatable: false,
      cells: {
         rowType: 'userRefundSummary',
         user: summary.user === 'Unknown' ? 'Unknown' : `${summary.user}`,
         userRaw: summary.user,
         count: summary.count,
         totalRefunded: formatCurrency(summary.totalAmount),
         totalRefundedRaw: summary.totalAmount
      }
   }));
}

function getHeaderTotal(rows: ReportRowVm[], layoutId?: string | null): number {
   if (layoutId === LAYOUT_REFUNDS_BY_USER) {
      return rows.reduce((total, row) => {
         const amount = Number(row.cells?.['totalRefundedRaw']);
         return Number.isFinite(amount) ? total + amount : total;
      }, 0);
   }

   return rows.reduce((total, row) => {
      const amount = Number(row.cells?.['amountRaw']);
      return Number.isFinite(amount) ? total + amount : total;
   }, 0);
}

export const PAYMENT_REFUNDS_DEF: ReportDefinition = {
   reportType: 'PaymentRefunds',
   version: 5,
   title: 'Payment Refunds',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Payment Refunds',
      snippets: ({ allRows, layoutId }) => {
         const totalRefunded = getHeaderTotal(allRows, layoutId);

         const count =
            layoutId === LAYOUT_REFUNDS_BY_USER
               ? allRows.filter(
                    (row) => row.cells?.['rowType'] === 'userRefundSummary'
                 ).length
               : allRows.filter((row) => row.cells?.['rowType'] === 'transaction')
                    .length;

         return [
            {
               label: layoutId === LAYOUT_REFUNDS_BY_USER ? 'Users' : 'Count',
               value: count
            },
            {
               label: 'Total Refunded',
               value: formatCurrency(totalRefunded),
               tone: totalRefunded < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['transaction']
   },

   annotations: false,

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'method',
         header: 'METHOD',
         width: '150px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'receipt',
         header: 'RECEIPT',
         width: '130px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '115px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'asc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'method', label: 'Method', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         { key: 'receipt', label: 'Receipt', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: flattenDetailedRefunds,

   layouts: [
      {
         id: LAYOUT_DETAILED_REFUNDS,
         label: 'Detailed Refunds',
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         },
         columns: [
            {
               key: 'date',
               header: 'TRN DATE',
               width: '105px',
               sort: {
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               }
            },
            {
               key: 'time',
               header: 'TIME',
               width: '75px',
               sort: { type: 'string' }
            },
            {
               key: 'room',
               header: 'ROOM',
               width: '80px',
               sort: { type: 'string' }
            },
            {
               key: 'guestName',
               header: 'GUEST NAME',
               width: '160px',
               sort: { type: 'string' }
            },
            {
               key: 'method',
               header: 'METHOD',
               width: '150px',
               sort: { type: 'string' }
            },
            {
               key: 'reason',
               header: 'REASON',
               width: '360px',
               maxChars: 130,
               sort: { type: 'string' }
            },
            {
               key: 'receipt',
               header: 'RECEIPT',
               width: '130px',
               sort: { type: 'string' }
            },
            {
               key: 'amount',
               header: 'AMOUNT',
               width: '115px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw'],
                  defaultDirection: 'asc'
               }
            },
            {
               key: 'user',
               header: 'USER',
               width: '140px',
               sort: { type: 'string' }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'date',
                  label: 'Transaction Date',
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               },
               { key: 'time', label: 'Time', type: 'string' },
               { key: 'room', label: 'Room', type: 'string' },
               { key: 'guestName', label: 'Guest Name', type: 'string' },
               { key: 'method', label: 'Method', type: 'string' },
               { key: 'reason', label: 'Reason', type: 'string' },
               { key: 'receipt', label: 'Receipt', type: 'string' },
               {
                  key: 'amount',
                  label: 'Amount',
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw']
               },
               { key: 'user', label: 'User', type: 'string' }
            ]
         },
         flattenRows: flattenDetailedRefunds
      },
      {
         id: LAYOUT_REFUNDS_BY_USER,
         label: 'Refunds by User',
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Refunds by User because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['userRefundSummary']
         },
         columns: [
            {
               key: 'user',
               header: 'USER',
               width: '220px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               }
            },
            {
               key: 'count',
               header: 'COUNT',
               width: '120px',
               sort: {
                  type: 'number',
                  defaultDirection: 'desc'
               }
            },
            {
               key: 'totalRefunded',
               header: 'TOTAL REFUNDED',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalRefundedRaw'],
                  defaultDirection: 'asc'
               }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'user',
                  label: 'User',
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               },
               {
                  key: 'count',
                  label: 'Count',
                  type: 'number'
               },
               {
                  key: 'totalRefunded',
                  label: 'Total Refunded',
                  type: 'number',
                  getValue: (row) => row.cells['totalRefundedRaw']
               }
            ]
         },
         flattenRows: flattenRefundsByUser
      }
   ]
};
